import { Uuid, DateISO, SelectOption } from 'types';
import { ISelectOptions } from 'framework/components/ui/FormComponents/Select';
import { Store } from 'framework/modules/stores/components/StoresFilter/types';
import { BaseOfferType, BaseOfferOption } from './offers';

export enum PromotionStatus {
  Inactive = 'inactive',
  Active = 'active',
  Scheduled = 'scheduled',
  Expired = 'expired',
}

export enum PromotionType {
  UndefinedPromotion = 'UndefinedPromotion',
  CartPromotion = 'CartPromotion',
  ProductPromotion = 'ProductPromotion',
  BuyXGetYPromotion = 'BuyXGetYPromotion',
  /**
   * Promotions fed via the API before the promotion types were created
   */
  Custom = 'Custom',
  BulkPromotion = 'BulkPromotion',
  BundlePromotion = 'BundlePromotion',
  TargetPromotion = 'TargetPromotion',
  FixedPricePromotion = 'FixedPricePromotion',
  WeightedPromotion = 'WeightedPromotion',
}

export enum PromotionTimeToUse {
  UseUtc = 'UseUtc',
  UseStoreTime = 'UseStoreTime',
}

export interface Promotion {
  id: Uuid;
  promotionRetailerId: string;
  displayName: string;
  shortDescription: string;
  startDate?: DateISO;
  endDate?: DateISO;
  priority: number;
  stores: Store[];
  modifiedOn: DateISO;
  promotionType: PromotionType;
  status: PromotionStatus;
  statusCanBeChanged: boolean;
  readOnly: boolean;
  promotionTimeToUse?: PromotionTimeToUse;
}

export type TierBase = {
  minCartValue?: number;
  minTotalCartValue?: number;
  howMany?: number;
} & {
  [k in BaseOfferType]?: number;
};

export enum ConditionsOptions {
  SKUs = 'requiredSkus',
  Brands = 'requiredBrands',
  Categories = 'requiredCategories',
  Attributes = 'requiredAttributes',
  /**
   * will possibly be removed when api is fixed
   */
  RedeemPoints = 'pointsRedeemed',
}

export interface OfferValue {
  offer: BaseOfferType | BaseOfferOption;
  value: number;
  howManyGetDiscount?: number;
  promoteCondition?: ConditionsOptions;
  promoteValues?: string[];
}

export interface PromotionUICondition {
  condition?: ConditionsOptions;
  howMany?: number;
  minCartValue?: number;
  minTotalCartValue?: number;
  value?: string[];
  groupHeader?: string;
}

export interface PromotionUITier {
  conditions: PromotionUICondition[];
  offers: OfferValue[];
}

export interface PromotionUIProps {
  tiers: PromotionUITier[];
}

export interface PromotionConditions {
  requiredSkus?: string[];
  requiredBrands?: string[];
  requiredCategories?: string[];
  requiredAttributes?: string[];
  groupHeader?: string;
}

export interface PromotionPromote {
  howManyGetDiscount?: number;
  promotedSkus?: string[];
  promotedBrands?: string[];
  promotedCategories?: string[];
  promotedAttributes?: string[];
}

export interface PromotionTier extends TierBase, PromotionConditions, PromotionPromote {}

interface Img {
  url: string;
  alt: string;
  title: string;
}

export interface PromotionRules extends PromotionTier {
  tiers?: TierBase[];
  groups?: (PromotionConditions & TierBase)[];
}

export interface PromotionDetailsBase extends PromotionRules {
  promotionId: Uuid;
  promotionRetailerId: string;
  name: string;
  shortDescription: string;
  additionalText: string;
  termAndConditions: string;
  galleryImage: Img;
  featuredImage: Img;
  mobileImage: Img;
  priority: number;
  stores: (Store | string)[];
  startDate: DateISO;
  endDate: DateISO;
  datesTimezone?: string;
  enabled?: boolean;
  customerExternalIds: string[];
  subscriptions: string[];
  segmentIdentifiers: string[];
  combineWithOtherPromotions?: boolean;
  onlyForLoyaltyUsers?: boolean;
  loyaltyWildCards?: string[];
  posBarcode?: string[];
  promotionType?: PromotionType;
  status: PromotionStatus;
  statusCanBeChanged?: boolean;
  promoCodes?: string[];
  showInListings?: boolean;
  limit?: boolean;
  serviceFeeToMatch?: number;
  limitPerSku?: number;
  missPromotionCompletionThreshold?: number;
  applyOnFirstOrders?: number;
  pointsRedeemed?: number;
  shoppingModes?: string[];
  readOnly?: boolean;
  promotionTimeToUse?: PromotionTimeToUse;
  /**
   * Field used to keep ui state. This prop won't be sent in the api request when saving
   */
  __ui__?: PromotionUIProps;
}

export interface PromotionForm extends Omit<PromotionDetailsBase, 'promoCodes'> {
  promoCodes: ISelectOptions[];
}

export interface PromotionOptionsBase {
  offers: BaseOfferOption[];
  promoCodes?: ISelectOptions[];
  conditions?: ISelectOptions<ConditionsOptions>[];
  shoppingModes?: ISelectOptions[];
  brands?: ISelectOptions[];
  attributes?: ISelectOptions<string>[];
  Subscriptions?: ISelectOptions[];
  activeSegments?: ISelectOptions[];
}

export interface FetchPromotionResponse {
  item: PromotionDetailsBase;
  options?: PromotionOptionsBase;
  readOnly?: boolean;
  promotionTimeToUse?: PromotionTimeToUse;
}

export interface FetchPromotionsOptions {
  status: SelectOption[];
  stores: SelectOption[];
}

export interface FetchPromotionsResponse {
  items: Promotion[];
  promotionTimeToUse: PromotionTimeToUse;
  total: number;
  options?: FetchPromotionsOptions;
}

export type PromotionsListParams = {
  name?: string;
  storeId?: string;
  status?: string;
};
